import { createFileRoute, Link, Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { listAiThreads, createAiThread, deleteAiThread } from "@/lib/ai-assistant.functions";
import { Button } from "@/components/ui/button";
import { Plus, Sparkles, Trash2, MessageSquare } from "lucide-react";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/_authenticated/admin/ai")({
  head: () => ({
    meta: [{ title: "AI Manager — Plunkets Admin" }, { name: "robots", content: "noindex" }],
  }),
  component: AiLayout,
});

function AiLayout() {
  const listFn = useServerFn(listAiThreads);
  const createFn = useServerFn(createAiThread);
  const deleteFn = useServerFn(deleteAiThread);
  const nav = useNavigate();
  const qc = useQueryClient();
  const params = useParams({ strict: false }) as { threadId?: string };
  const activeId = params.threadId;

  const { data: threads } = useQuery({
    queryKey: ["ai-threads"],
    queryFn: () => listFn(),
  });

  const create = useMutation({
    mutationFn: () => createFn({ data: {} }),
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["ai-threads"] });
      if (t) nav({ to: "/admin/ai/$threadId", params: { threadId: t.id } });
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => deleteFn({ data: { id } }),
    onSuccess: (_, id) => {
      qc.invalidateQueries({ queryKey: ["ai-threads"] });
      if (activeId === id) nav({ to: "/admin/ai" });
    },
  });

  return (
    <div className="grid h-[calc(100dvh-3.5rem)] grid-cols-1 md:grid-cols-[260px_1fr]">
      <aside className="hidden flex-col border-r border-border bg-muted/20 md:flex">
        <div className="flex items-center justify-between p-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-primary" />
            <span className="text-sm font-semibold">Plunkets AI</span>
          </div>
          <Button size="sm" variant="ghost" onClick={() => create.mutate()} disabled={create.isPending}>
            <Plus className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex-1 space-y-1 overflow-y-auto px-2 pb-3">
          {threads?.length === 0 && (
            <p className="px-2 py-4 text-xs text-muted-foreground">No conversations yet.</p>
          )}
          {threads?.map((t) => (
            <div
              key={t.id}
              className={cn(
                "group flex items-center gap-2 rounded-md px-2 py-1.5 text-sm hover:bg-muted",
                activeId === t.id && "bg-muted",
              )}
            >
              <Link
                to="/admin/ai/$threadId"
                params={{ threadId: t.id }}
                className="flex flex-1 items-center gap-2 truncate"
              >
                <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{t.title}</span>
              </Link>
              <button
                type="button"
                onClick={() => remove.mutate(t.id)}
                className="opacity-0 transition-opacity group-hover:opacity-100"
                aria-label="Delete conversation"
              >
                <Trash2 className="h-3.5 w-3.5 text-muted-foreground hover:text-destructive" />
              </button>
            </div>
          ))}
        </div>
      </aside>
      <main className="min-h-0 overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
